import React from 'react';
import PropTypes from 'prop-types';
import { IoLogoFacebook } from 'react-icons/io';

import { __t } from '@mssgme/shared';
import { handleError, facebookApiAdapter, reviewsApi } from '@mssgme/lib';
import { Button, Notification } from '@mssgme/ui';

import { CardReviewRecommendModal } from '../CardReviewRecommendModal';
import { CardReviewMessageModal } from '../CardReviewMessageModal';
import CardReviewsPane from './component';

function normalize(review) {
    review.createdAt = new Date(review.createdAt);
    review.updatedAt = new Date(review.updatedAt);

    return review;
}

export default class CardReviewsPaneContainer extends React.Component {
    static propTypes = {
        workspaceId: PropTypes.string.isRequired,
        previewMode: PropTypes.bool,
        fullName: PropTypes.string,
    };

    state = {
        reviews: [],
        next: null,
        isFetching: true,
        isLoaded: false,
        hasNext: false,
        recommends: null,
        recommend: true,
        isAuthenticated: false,
        facebookId: null,
        myReviews: [],
        showModals: {
            review: false,
            login: false,
            notify: false,
            confirm: false,
            success: false,
        },
    };

    async componentDidMount() {
        await this.init();
    }

    async init() {
        try {
            this.fbApi = await facebookApiAdapter();

            await this.checkStatus();
            await Promise.all([this.loadReviews(), this.loadMyReviews()]);

            this.setState({ isLoaded: true });
        } catch (e) {
            handleError(e);
        }
    }

    checkStatus = async () => {
        const json = await this.handleAuthRequest(this.fbApi.getLoginStatus());
        this.setState(json);
    };

    handleLoginClick = async (e) => {
        e.preventDefault();

        try {
            let json = await this.handleAuthRequest(this.fbApi.login({ scope: 'public_profile,email' }));
            this.setState({ json });

            if (json.isAuthenticated) {
                json = await this.loadMyReviews();

                this.showModal(json.length > 0 ? 'notify' : 'review');
            }
        } catch (e) {
            handleError(e);
        }
    };

    handleAuthRequest = async (promise) => {
        let { status, authResponse } = await promise;

        if (status === 'connected') {
            reviewsApi.accessToken = authResponse.accessToken;
            setTimeout(this.checkStatus, authResponse.expiresIn * 1000);

            return { isAuthenticated: true, facebookId: authResponse.userID };
        } else {
            reviewsApi.accessToken = null;
            return { isAuthenticated: false, facebookId: null };
        }
    };

    loadReviews = async () => {
        const { next, hasNext, recommends } = this.state;
        const { workspaceId } = this.props;
        const urlSearchParams = {};

        if (next && hasNext) urlSearchParams.next = next;
        if (!recommends) {
            urlSearchParams.withRecommends = true;
        }

        this.setState({ isFetching: true });

        const json = await reviewsApi.list(workspaceId, urlSearchParams);

        this.setState((state) => {
            const newState = {
                isFetching: false,
                hasNext: json.hasNext,
                next: json.next,
                reviews: state.reviews.concat(json.results.map(normalize)),
            };

            if (json.recommends) newState.recommends = json.recommends;

            return newState;
        });
    };

    loadMyReviews = async () => {
        if (reviewsApi.accessToken) {
            const { workspaceId } = this.props;
            const json = await reviewsApi.my(workspaceId);

            this.setState({ myReviews: json });

            return json;
        } else {
            return [];
        }
    };

    onRecommend = (recommend) => {
        this.setState({ recommend });
        this.showModal(this.state.isAuthenticated ? 'review' : 'login');
    };

    closeModal = (target) => {
        this.setState((state) => ({
            showModals: {
                ...state.showModals,
                [target]: false,
            },
        }));
    };

    showModal = (target) => {
        this.setState((state) => {
            const newShowModals = {};
            for (let prop in state.showModals) {
                newShowModals[prop] = prop === target;
            }

            return {
                showModals: newShowModals,
            };
        });
    };

    onDelete = async (review) => {
        try {
            const { workspaceId } = this.props;

            await reviewsApi.delete(workspaceId, review._id);

            this.setState((state) => {
                const recommends = [...state.recommends];
                const index = review.recommend ? 0 : 1;

                recommends[index]--;

                return {
                    recommends,
                    reviews: state.reviews.filter(({ _id }) => _id !== review._id),
                    myReviews: state.myReviews.filter(({ _id }) => _id !== review._id),
                };
            });

            Notification.danger(__t('review_deleted'), { showClose: true });
        } catch (e) {
            handleError(e);
        }
    };

    onSubmit = async (values) => {
        try {
            const { workspaceId } = this.props;
            const json = await reviewsApi.create(workspaceId, values);
            const review = normalize(json);

            this.setState((state) => {
                const recommends = [...state.recommends];
                const index = review.recommend ? 0 : 1;

                recommends[index]++;

                return {
                    recommends,
                    myReviews: [review].concat(state.myReviews),
                };
            });

            this.closeModal('review');

            Notification.notify(__t('review_published'), { showClose: true });
        } catch (e) {
            handleError(e);
        }
    };

    render() {
        const {
            reviews,
            isFetching,
            hasNext,
            recommends,
            isAuthenticated,
            facebookId,
            showModals,
            recommend,
            isLoaded,
            myReviews,
        } = this.state;
        const { previewMode, fullName } = this.props;

        return (
            <CardReviewsPane
                isLoaded={isLoaded}
                recommends={recommends}
                reviews={reviews}
                myReviews={myReviews}
                isFetching={isFetching}
                hasNext={hasNext}
                previewMode={previewMode}
                actions={{
                    loadMore: this.loadReviews,
                    onRecommend: this.onRecommend,
                    onDelete: this.onDelete,
                }}
                fullName={fullName}
                facebookId={isAuthenticated && facebookId}
                {...this.props}
            >
                {showModals.review && (
                    <CardReviewRecommendModal
                        recommend={recommend}
                        fullName={fullName}
                        closeModalFn={this.closeModal.bind(this, 'review')}
                        onSubmit={this.onSubmit}
                    />
                )}
                {showModals.login && (
                    <CardReviewMessageModal closeModalFn={this.closeModal.bind(this, 'login')}>
                        <h1>{__t('review_login')}</h1>
                        <Button
                            onClick={this.handleLoginClick}
                            btnStyle="facebook"
                            fullWidth
                            beforeIcon={<IoLogoFacebook size={40} />}
                            iconFixed="left"
                        >
                            {__t('continue_dacebook')}
                        </Button>
                    </CardReviewMessageModal>
                )}
                {showModals.notify && (
                    <CardReviewMessageModal closeModalFn={this.closeModal.bind(this, 'notify')}>
                        <h1>{__t('have_review')}</h1>
                        <p>{__t('only_one_review')}</p>
                    </CardReviewMessageModal>
                )}
            </CardReviewsPane>
        );
    }
}
